import "@progress/kendo-ui/js/kendo.upload";
import * as ko from "knockout";
import * as Global from "../common/global";
import * as UI from "../common/ui";
import * as TargetData from "../data/targetData";
import { IApplication } from "../interfaces/application";
import { IController } from "../interfaces/controller";
import * as Data from "../interfaces/data";

interface ITargetImportViewModel {
    CurrentStep: KnockoutObservable<number>;
    TargetTemplateURLName: KnockoutObservable<string>;
    TargetTemplateName: KnockoutObservable<string>;
    TargetTemplateID: KnockoutObservable<number>;
    FormItems: KnockoutObservable<TargetData.ITargetMasterFormItem[]>;
    UploadedFileKey: KnockoutObservable<string>;
    TemplateDownloadUrl: KnockoutObservable<string>;
    VerificationResults: KnockoutObservable<TargetData.ITargetImportVerificationResult>;
    ImportCompleted: KnockoutObservable<boolean>;
    NumberOfTargetsImported: KnockoutObservable<number>;
    VerifyUploadedFile: () => void;
    BackToStep1: () => void;
    ImportRecords: () => void;
    Finished: () => void;
}

export class TargetImportController implements IController {
    // private uploadControl: kendo.ui.Upload;
    private container: JQuery;
    private viewModel: ITargetImportViewModel;
    private uploadControl: kendo.ui.Upload;
    private fieldsOnLoad: any = {};

    public constructor() {
        const me: TargetImportController = this;
        me.viewModel = {
            CurrentStep: ko.observable(1),
            TargetTemplateID: ko.observable(),
            TargetTemplateName: ko.observable(),
            TargetTemplateURLName: ko.observable(),
            FormItems: ko.observable(),
            UploadedFileKey: ko.observable(),
            TemplateDownloadUrl: ko.observable(),
            VerificationResults: ko.observable(),
            ImportCompleted: ko.observable(false),
            NumberOfTargetsImported: ko.observable(0),
            VerifyUploadedFile: me.verifyUpload.bind(me),
            BackToStep1: me.backToStep1.bind(me),
            ImportRecords: me.importRecords.bind(me),
            Finished: me.finished.bind(me),
        };
    }

    public render(container: JQuery): JQueryPromise<any> {
        const me: TargetImportController = this;
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();
        const app: IApplication = Global.app();

        me.container = container;
        ko.applyBindings(this.viewModel, container[0]);

        me.uploadControl = this.container.find(".uploadFiles").kendoUpload({
            async: {
                saveUrl: "save",
                autoUpload: true,
            },
            validation: {
                allowedExtensions: [".csv", ".xls", ".xlsx"],
            },
            upload: (e: kendo.ui.UploadUploadEvent) => {
                me.viewModel.UploadedFileKey(null);
                if (e.sender.options.async) {
                    e.sender.options.async.saveUrl = app.webServiceBaseURL + "/api/Targets/Import/" + this.viewModel.TargetTemplateID();
                }
            },
            success: (e: kendo.ui.UploadSuccessEvent) => {
                if (e.operation === "upload" && e.response) {
                    me.viewModel.UploadedFileKey(e.response);
                }
            },
            multiple: false,
        }).data("kendoUpload");

        deferred.resolve();

        return deferred.promise();
    }

    public load(params: any[]): JQueryPromise<any> {
        const app: IApplication = Global.app();
        const me: TargetImportController = this;
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();
        const waitContainer: JQuery<HTMLElement> = UI.showWaitIndicator(this.container);

        if (params == null || params.length !== 1) {
            // invalid parameters passed into this page
            app.pageManager().closeCurrentPage();
            deferred.resolve();
            return deferred.promise();
        }

        const targetTemplateURLName = params[0];

        TargetData.GetImportConfiguration(targetTemplateURLName)
            .done((response: Data.IResponse<TargetData.ITargetImportConfiguration>) => {
                if (response.Success) {
                    this.viewModel.TargetTemplateID(response.Payload.TargetTemplateID);
                    this.viewModel.TargetTemplateName(response.Payload.TargetTemplateName);
                    this.viewModel.TargetTemplateURLName(response.Payload.TargetTemplateURLName);
                    this.viewModel.TemplateDownloadUrl(`${app.webServiceBaseURL}/api/Targets/DownloadImportTemplate/${response.Payload.TargetTemplateID}`);
                    this.viewModel.FormItems(response.Payload.FormItems);
                } else {
                    UI.showErrorMessage(response.ErrorMessages);
                }
            })
            .always(() => {
                UI.hideWaitIndicator(me.container, waitContainer);

                // remember the fields value so we can determine if they have changed
                me.fieldsOnLoad = ko.toJSON(this.viewModel);

                deferred.resolve();
            });

        return deferred.promise();
    }

    public haveFieldsChanged(): boolean {
        const fieldValues: string = ko.toJSON(this.viewModel);
        return fieldValues !== this.fieldsOnLoad;
    }

    public appRouterID(params: any[]): string {
        let targetName: string = "UNKNOWN";
        if (params.length > 0) {
            targetName = params[0];
        }
        return "TargetsImport" + targetName;
    }

    public title(): string {
        return "Import Targets";
    }

    public subTitle(): string {
        if (this.viewModel) {
            return this.viewModel.TargetTemplateName();
        }
        return "";
    }

    private verifyUpload() {
        const me: TargetImportController = this;
        me.viewModel.CurrentStep(2);

        const waitContainer: JQuery<HTMLElement> = UI.showWaitIndicator(this.container);

        TargetData.ValidateTargetImportFile(me.viewModel.TargetTemplateID(), me.viewModel.UploadedFileKey())
            .done((response: Data.IResponse<TargetData.ITargetImportVerificationResult>) => {
                if (response.Success) {
                    me.viewModel.VerificationResults(response.Payload);
                } else {
                    UI.showErrorMessage(response.ErrorMessages);
                }
            })
            .always(() => {
                UI.hideWaitIndicator(me.container, waitContainer);
            });
    }

    private backToStep1() {
        const me: TargetImportController = this;
        me.viewModel.UploadedFileKey("");
        me.viewModel.VerificationResults(null);
        me.uploadControl.clearAllFiles();
        me.viewModel.CurrentStep(1);
    }

    private importRecords() {
        const me: TargetImportController = this;

        UI.showConfirmDialog("Are you sure you want to import these targets?",
            () => {

                me.viewModel.CurrentStep(3);

                TargetData.ProcessImport(me.viewModel.TargetTemplateID(), me.viewModel.UploadedFileKey())
                    .done((response: Data.IResponse<number>) => {
                        if (response.Success) {
                            me.viewModel.ImportCompleted(true);
                            me.viewModel.NumberOfTargetsImported(response.Payload);
                        } else {
                            UI.showErrorMessage(response.ErrorMessages);
                        }
                    });
            });
    }

    private finished() {
        Global.app().pageManager().closeAndNavigate("/Targets/" + this.viewModel.TargetTemplateURLName());
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--7!./src/script/controllers/targetImport.ts